import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Camera, Video, Images, X, Play, Loader2 } from "lucide-react";

const ACCEPT = { image: "image/*", video: "video/*", media: "image/*,video/*" };
const isVideo = (x) => (x?.mime || "").startsWith("video/") || /\.(mp4|mov|webm|m4v|3gp)$/i.test(x?.filename || "");

function Thumb({ item, onOpen, onRemove, disabled, testid }) {
  const [url, setUrl] = useState(item.preview || null);
  useEffect(() => {
    if (!url && item.attachment_id) api.get(`/attachments/${item.attachment_id}/view`).then((r) => setUrl(r.data.url)).catch(() => {});
  }, [item.attachment_id]); // eslint-disable-line react-hooks/exhaustive-deps
  const vid = isVideo(item);
  return (
    <div className="relative w-24 h-24 rounded-lg overflow-hidden border border-slate-200 bg-slate-100 group" data-testid={testid}>
      <button type="button" className="w-full h-full cursor-pointer" onClick={() => url && onOpen({ url, vid, name: item.filename })} disabled={!url} title={item.filename}>
        {url ? (vid ? <video src={url} className="w-full h-full object-cover" muted preload="metadata" /> : <img src={url} alt={item.filename} className="w-full h-full object-cover" />)
          : <span className="flex w-full h-full items-center justify-center text-[10px] text-slate-400 px-1 text-center">{item.uploading ? "Uploading…" : "Scanning…"}</span>}
        {vid && url && <Play className="absolute inset-0 m-auto w-7 h-7 text-white drop-shadow" />}
      </button>
      {item.uploading && <Loader2 className="absolute top-1 left-1 w-4 h-4 animate-spin text-indigo-600" />}
      {!disabled && !item.uploading && (
        <button type="button" onClick={onRemove} className="absolute top-1 right-1 rounded-full bg-white/90 p-0.5 text-slate-600 hover:text-rose-600 cursor-pointer" data-testid={`${testid}-remove`}>
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

// Multi photo/video capture for forms: camera, gallery, thumbnails, preview, remove. Uploads go through the
// existing scanned /attachments/upload pipeline against the job (Job Files).
export function FormMediaField({ field, list, disabled, onAdd, onRemove }) {
  const [open, setOpen] = useState(null);
  const cam = useRef(null), rec = useRef(null), gal = useRef(null);
  const max = field.max_files || 30;
  const kind = field.accept === "image" || field.accept === "video" ? field.accept : field.accept === "media" ? "media" : null;
  const left = Math.max(0, max - list.length);
  const pick = (e) => { Array.from(e.target.files || []).slice(0, left).forEach(onAdd); e.target.value = ""; };
  const btn = "inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 cursor-pointer disabled:opacity-50";
  return (
    <div data-testid={`media-field-${field.id}`}>
      {!disabled && (
        <div className="flex flex-wrap gap-2 mb-2">
          {kind !== "video" && kind && <button type="button" className={btn} disabled={!left} onClick={() => cam.current.click()} data-testid={`media-camera-${field.id}`}><Camera className="w-4 h-4" />Take photo</button>}
          {kind !== "image" && kind && <button type="button" className={btn} disabled={!left} onClick={() => rec.current.click()} data-testid={`media-record-${field.id}`}><Video className="w-4 h-4" />Record video</button>}
          <button type="button" className={btn} disabled={!left} onClick={() => gal.current.click()} data-testid={`media-gallery-${field.id}`}><Images className="w-4 h-4" />{kind ? "Choose from gallery" : "Choose files"}</button>
          <span className="self-center text-xs text-slate-400" data-testid={`media-count-${field.id}`}>{list.length} / {max}</span>
          <input ref={cam} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid={`file-input-camera-${field.id}`} />
          <input ref={rec} type="file" accept="video/*" capture="environment" className="hidden" onChange={pick} data-testid={`file-input-record-${field.id}`} />
          <input ref={gal} type="file" multiple accept={kind ? ACCEPT[kind] : undefined} className="hidden" onChange={pick} data-testid={`file-input-${field.id}`} />
        </div>
      )}
      <div className="flex flex-wrap gap-2" data-testid={`file-list-${field.id}`}>
        {list.map((x, i) => <Thumb key={x.tmp || x.attachment_id || i} item={x} disabled={disabled} onOpen={setOpen} onRemove={() => onRemove(i)} testid={`media-thumb-${field.id}-${i}`} />)}
        {disabled && !list.length && <div className="text-sm text-slate-500">No files</div>}
      </div>
      {open && (
        <div className="fixed inset-0 z-[80] bg-slate-900/80 flex items-center justify-center p-4" onClick={() => setOpen(null)} data-testid="media-preview">
          <div className="max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            {open.vid ? <video src={open.url} controls autoPlay className="w-full max-h-[80vh] rounded-lg bg-black" /> : <img src={open.url} alt={open.name} className="w-full max-h-[80vh] object-contain rounded-lg" />}
            <button type="button" onClick={() => setOpen(null)} className="mt-2 text-sm text-white underline cursor-pointer" data-testid="media-preview-close">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

export function YesNoNa({ field, value, disabled, onChange, state }) {
  const opts = ["Yes", "No", ...(field.allow_na ? ["N/A"] : [])];
  const tone = { Yes: "bg-emerald-600 border-emerald-600 text-white", No: "bg-rose-600 border-rose-600 text-white", "N/A": "bg-slate-600 border-slate-600 text-white" };
  const cur = value === "N/A" ? "N/A" : state;
  return (
    <div className="flex gap-2" data-testid={`yesno-${field.id}`}>
      {opts.map((o) => (
        <button key={o} type="button" disabled={disabled} onClick={() => onChange(o)} data-testid={`yesno-${field.id}-${o.replace("/", "").toLowerCase()}`}
          className={`min-w-[72px] rounded-xl border-2 px-4 py-2.5 text-sm font-bold cursor-pointer disabled:cursor-default ${cur === o ? tone[o] : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>{o}</button>
      ))}
    </div>
  );
}
